import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { listTtsVoices } from '../features/scenarios/api';
import { useCreditRates } from '../features/organizations/hooks';
import { Download } from 'lucide-react';
import { VoiceSelect } from './generation/scenario/VoiceSelect';
import { GenerateButton } from './generation/common/GenerateButton';
import {
  AudioPlayButton,
  formatAudioDuration,
  probeAudioDuration,
  downloadAudio,
} from './common/AudioPlayButton';
import { NARRATION_RESOURCE_TYPE_BY_PROVIDER, type NarrationProviderCode } from './generation/scenario/constants';
import styles from './SoundLogoGenerateModal.module.css';
import '../styles/buttons.css';

/** VoiceSelect が要求するボイス形。listTtsVoices の型引数として使う。 */
interface Voice {
  voice_id: string;
  name: string;
  labels?: Record<string, string>;
  category?: string;
  preview_url?: string;
  high_quality_base_model_ids?: string[];
}

type NarrationProvider = NarrationProviderCode;
const RESOURCE_TYPE_BY_PROVIDER = NARRATION_RESOURCE_TYPE_BY_PROVIDER;

/** /generate が返す仮テイク。登録するまでサーバー側には何も残らない。 */
interface GeneratedTake {
  key: string;
  audioBase64: string;
  dataUrl: string;
  text: string;
  voiceId: string;
  voiceName: string;
  provider: NarrationProvider;
  language: 'ja' | 'en';
  durationSeconds: number | null;
  modelId?: string;
}

interface GenerateResponse {
  audio_base64: string;
  mime_type: string;
  text: string;
  voice_id: string;
  provider: NarrationProvider;
  language: 'ja' | 'en';
  model_id?: string;
}

interface SoundLogoGenerateModalProps {
  brandId: string;
  brandName?: string;
  tagline?: string;
  onClose: () => void;
  /** 登録成功時に呼ばれる（親側で一覧を invalidate する） */
  onGenerated: () => void;
}

/**
 * サウンドロゴの AI 生成モーダル。2カラム構成。
 * 左: 生成設定 (テキスト / ナレーター) + 生成ボタン。シナリオ編集のナレーション
 * 生成と同じプロバイダ (ElevenLabs / Gemini) とナレーター選択 UI を使う。
 * 右: 生成した仮テイクの一覧 (この時点で素材化されない)。
 *
 * 試聴してチェックを入れたテイクだけが、フッターの「登録する」で永続化されて
 * ブランドに紐付く (複数可)。チェックしなかったテイクは閉じると破棄される。
 */
export default function SoundLogoGenerateModal({
  brandId,
  brandName,
  tagline,
  onClose,
  onGenerated,
}: SoundLogoGenerateModalProps) {
  const toast = useToast();
  const [text, setText] = useState(brandName ?? '');
  const [provider, setProvider] = useState<NarrationProvider>('elevenlabs');
  const [voiceId, setVoiceId] = useState('');
  const [englishPronunciation, setEnglishPronunciation] = useState(false);
  const [takes, setTakes] = useState<GeneratedTake[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [generating, setGenerating] = useState(false);
  const [registering, setRegistering] = useState(false);

  // シナリオ編集画面と同じ queryKey でキャッシュを共有する
  const { data: voices = [] } = useQuery({
    queryKey: ['tts-voices', provider],
    queryFn: () => listTtsVoices<Voice>(provider),
    staleTime: 60 * 60 * 1000, // 1時間キャッシュ
  });

  const { data: creditRatesData } = useCreditRates();
  const creditCost = useMemo(() => {
    const rate = creditRatesData?.rates?.find(
      (r) => r.service_type === 'narration' && r.resource_type === RESOURCE_TYPE_BY_PROVIDER[provider],
    );
    return rate ? Math.ceil(rate.credits_per_unit) : 1;
  }, [creditRatesData, provider]);

  // プロバイダ切替時は選択中ボイスをリセット（ボイス ID はプロバイダ固有のため）。
  const handleProviderChange = (next: NarrationProvider) => {
    setProvider(next);
    setVoiceId('');
  };

  // 仮テイクを生成してリスト先頭に追加する（この時点では課金のみで素材化されない）
  const handleGenerate = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      toast.error('読み上げテキストを入力してください');
      return;
    }
    if (!voiceId) {
      toast.error('ナレーターを選択してください');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await apiClient.post<GenerateResponse>(
        `/brands/${brandId}/sound-logos/generate`,
        {
          text: trimmed,
          voice_id: voiceId,
          provider,
          language: englishPronunciation ? 'en' : 'ja',
        },
      );
      const voiceName = voices.find((v) => v.voice_id === data.voice_id)?.name ?? data.voice_id;
      const dataUrl = `data:${data.mime_type || 'audio/mpeg'};base64,${data.audio_base64}`;
      // 尺は data URL のメタデータから取得 (ネットワークコストなし)
      const durationSeconds = await probeAudioDuration(dataUrl);
      setTakes((prev) => [
        {
          key: `${Date.now()}-${prev.length}`,
          audioBase64: data.audio_base64,
          dataUrl,
          text: data.text,
          voiceId: data.voice_id,
          voiceName,
          provider: data.provider,
          language: data.language,
          durationSeconds,
          modelId: data.model_id,
        },
        ...prev,
      ]);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err instanceof Error ? err.message : 'サウンドロゴの生成に失敗しました');
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const toggleSelected = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // チェックしたテイクを順に永続化してブランドに紐付ける（ここで初めて素材が作られる）。
  // 全件成功でモーダルを閉じる (チェックしなかったテイクは意図的な破棄とみなす)。
  // 途中で失敗したら、登録済み分を一覧から除いたうえでモーダルを開いたままにする。
  const handleRegisterSelected = async () => {
    const selected = takes.filter((t) => selectedKeys.has(t.key));
    if (selected.length === 0) return;
    setRegistering(true);
    const registeredKeys: string[] = [];
    let failedMessage: string | null = null;
    for (const take of selected) {
      try {
        await apiClient.post(`/brands/${brandId}/sound-logos/register`, {
          audio_base64: take.audioBase64,
          text: take.text,
          voice_id: take.voiceId,
          voice_name: take.voiceName,
          provider: take.provider,
          language: take.language,
          model_id: take.modelId,
        });
        registeredKeys.push(take.key);
      } catch (err: unknown) {
        failedMessage =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          (err instanceof Error ? err.message : 'サウンドロゴの登録に失敗しました');
        break;
      }
    }
    if (registeredKeys.length > 0) {
      onGenerated();
      setTakes((prev) => prev.filter((t) => !registeredKeys.includes(t.key)));
      setSelectedKeys((prev) => new Set([...prev].filter((k) => !registeredKeys.includes(k))));
    }
    setRegistering(false);
    if (failedMessage) {
      toast.error(failedMessage);
      return;
    }
    toast.success(
      registeredKeys.length > 1
        ? `${registeredKeys.length}件のサウンドロゴを登録しました`
        : 'サウンドロゴを登録しました',
    );
    onClose();
  };

  // 未登録テイクが残っている場合は破棄確認してから閉じる
  const handleClose = () => {
    if (takes.length > 0) {
      if (!window.confirm('生成したテイクは登録されていません。\n破棄して閉じますか？')) return;
    }
    onClose();
  };

  const selectedCount = takes.filter((t) => selectedKeys.has(t.key)).length;

  return createPortal(
    // 入力内容の消失を防ぐため、オーバーレイのクリックでは閉じない (✕ ボタンのみ)
    <div className="modal-overlay modal-overlay--nested">
      <div className="modal-card modal-card--lg">
        <div className="modal-header">
          <h2 className="modal-title">サウンドロゴを生成</h2>
          <button
            type="button"
            className="modal-close-button"
            onClick={handleClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.columns}>
            {/* 左カラム: 生成設定 + 生成ボタン */}
            <div className={styles.colLeft}>
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="sl-text">
                  読み上げテキスト<span className={styles.required}>*</span>
                </label>
                <input
                  id="sl-text"
                  className={styles.input}
                  value={text}
                  maxLength={200}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="ブランド名やスローガンなど"
                />
                <span className={styles.fieldHint}>
                  ブランド名やスローガンなど、サウンドロゴとして読み上げる短いフレーズを入力してください。
                  {tagline && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className={styles.inlineLink}
                        onClick={() => setText(tagline)}
                      >
                        スローガンを使う
                      </button>
                    </>
                  )}
                </span>
              </div>

              <div className={styles.field}>
                <label className={styles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={englishPronunciation}
                    onChange={(e) => setEnglishPronunciation(e.target.checked)}
                  />
                  <span>英語で発音する</span>
                </label>
                <span className={styles.fieldHint}>英字のブランド名を英語ネイティブの読みで発音します。</span>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>
                  ナレーター<span className={styles.required}>*</span>
                </span>
                <VoiceSelect
                  voices={voices}
                  value={voiceId}
                  onChange={setVoiceId}
                  provider={provider}
                  onProviderChange={handleProviderChange}
                />
              </div>

              {/* 生成ボタンは左カラム末尾に固定 (生成部と列挙部と submit の分離) */}
              <div className={styles.generateRow}>
                <GenerateButton
                  label={takes.length > 0 ? 'もう一度生成' : '生成する'}
                  creditCost={creditCost}
                  isLoading={generating}
                  loadingLabel="生成中"
                  showLoadingDots
                  onClick={handleGenerate}
                  disabled={generating || registering || !text.trim() || !voiceId}
                />
              </div>
            </div>

            {/* 右カラム: 生成した仮テイクの一覧 */}
            <div className={styles.colRight}>
              <span className={styles.fieldLabel}>生成したテイク</span>
              {takes.length === 0 ? (
                <span className={styles.fieldHint}>
                  まだテイクがありません。左の設定で「生成する」を押すと、ここに仮テイクが追加されます。
                </span>
              ) : (
                <>
                  <span className={styles.fieldHint}>
                    試聴して、登録したいテイクにチェックを入れてください。チェックしなかったテイクは破棄されます。
                  </span>
                  <div className={styles.takeList}>
                    {takes.map((take, index) => (
                      <div key={take.key} className={styles.takeRow}>
                        <input
                          type="checkbox"
                          className={styles.takeCheckbox}
                          checked={selectedKeys.has(take.key)}
                          onChange={() => toggleSelected(take.key)}
                          disabled={registering}
                          aria-label={`${take.voiceName} のテイクを登録対象にする`}
                        />
                        <AudioPlayButton src={take.dataUrl} />
                        <span className={styles.takeDuration}>
                          {formatAudioDuration(take.durationSeconds)}
                        </span>
                        <span className={styles.takeMeta} title={`「${take.text}」`}>
                          <span className={styles.takeVoice}>{take.voiceName}</span>
                        </span>
                        <button
                          type="button"
                          className={styles.takeDownload}
                          onClick={() => downloadAudio(take.dataUrl, `sound-logo-take-${takes.length - index}.mp3`)}
                          title="ダウンロード"
                          aria-label="ダウンロード"
                        >
                          <Download size={14} aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button type="button" className="btn-modal btn-modal-secondary" onClick={handleClose}>
            キャンセル
          </button>
          <button
            type="button"
            className="btn-modal"
            onClick={handleRegisterSelected}
            disabled={registering || selectedCount === 0}
          >
            {registering
              ? '登録中…'
              : selectedCount > 1
                ? `登録する (${selectedCount}件)`
                : '登録する'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
